import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {JSONValue} from 'util/Types';

import {
  BANNER_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  NUM_COLUMNS_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchemaV2NestedShapeValues,
  StepConfigEditorOverride,
} from '../../StepConfigEditorTypes';
import {isSeriesColumnNameNull} from '../StepConfigDisableFieldFunctions';
import {maybeGetValueFromConfig} from '../StepConfigSettingsUtils';

/* @package */
export const axisMinMaxBannerVisibilityFn = (config: Record<string, JSONValue>): boolean => {
  const yAxisIncludesZero = maybeGetValueFromConfig(config, [
    'chartConfig',
    'parameters',
    'yAxisIncludesZero',
  ]);
  const yAxisMin = maybeGetValueFromConfig(config, ['chartConfig', 'parameters', 'yAxisMin']);
  const yAxisMax = maybeGetValueFromConfig(config, ['chartConfig', 'parameters', 'yAxisMax']);
  return Boolean(yAxisIncludesZero) && (Boolean(yAxisMin) || Boolean(yAxisMax));
};

/**
 * Scatter Chart configuration sections for the Configure tab
 */
export const SCATTER_CHART_CONFIG_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['axesSection', 'seriesSection', 'errorBarsSection', 'colorPaletteSection'],
  axesSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisSection', 'yAxisSection'],
    xAxisSection: {
      displayName: 'X-axis',
      configShapePath: ['chartConfig', 'parameters', 'xAxisColumnName'],
    },
    yAxisSection: {
      displayName: 'Y-axis',
      configShapePath: ['chartConfig', 'parameters', 'yAxisColumnName'],
    },
  },
  seriesSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['seriesColumnName'],
    seriesColumnName: {
      displayName: 'Series',
      configShapePath: ['chartConfig', 'parameters', 'seriesColumnName'],
      fieldSettings: {
        allowSelectNull: true,
        noColumnSelectedText: 'None',
      },
    },
  },
  errorBarsSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['errorBarColumnName'],
    errorBarColumnName: {
      displayName: 'Error bars',
      configShapePath: ['chartConfig', 'parameters', 'errorBarColumnName'],
      fieldSettings: {
        allowSelectNull: true,
        noColumnSelectedText: 'None',
      },
    },
  },
  colorPaletteSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['colorPalette'],
    colorPalette: {
      disableFn: isSeriesColumnNameNull,
      displayName: 'Color palette',
      configShapePath: ['chartConfig', 'parameters', 'colorPalette'],
      fieldSettings: {
        displayNames: {
          DISCRETE: 'Discrete colors',
          GRADIENT: 'Continuous gradient',
        },
        svgImagePaths: {
          DISCRETE: '/static/img/insights/palettes/benchling.svg',
          GRADIENT: '/static/img/insights/palettes/gradient.svg',
        },
      },
    },
  },
} as const satisfies ShapeUISchemaV2NestedShapeValues;

/**
 * Scatter Chart style sections for the Style tab
 */
export const SCATTER_CHART_STYLE_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisSection', 'yAxisSection'],
  xAxisSection: {
    [SECTION_NAME_UI_SCHEMA_KEY]: 'X-axis',
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisScale'],
    xAxisScale: {
      displayName: 'Scale',
      configShapePath: ['chartConfig', 'parameters', 'xAxisScale'],
      fieldSettings: {
        displayNames: {
          LINEAR: 'Linear',
          LOG: 'Log',
        },
      },
    },
  },
  yAxisSection: {
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Y-axis',
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisScale', 'yAxisIncludesZero', 'yAxisRange'],
    yAxisScale: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisScale'],
      yAxisScale: {
        displayName: 'Scale',
        configShapePath: ['chartConfig', 'parameters', 'yAxisScale'],
        fieldSettings: {
          displayNames: {
            LINEAR: 'Linear',
            LOG: 'Log',
          },
        },
      },
    },
    yAxisIncludesZero: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisIncludesZero'],
      yAxisIncludesZero: {
        configShapePath: ['chartConfig', 'parameters', 'yAxisIncludesZero'],
        editorOverride: StepConfigEditorOverride.CHECKBOX,
        fieldSettings: {
          checkboxLabel: 'Include 0',
        },
      },
    },
    yAxisRange: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisMin', 'yAxisMax'],
      [SECTION_NAME_UI_SCHEMA_KEY]: 'Y-Axis Range',
      [NUM_COLUMNS_KEY]: 2,
      [BANNER_KEY]: {
        text: 'Including 0 on the y-axis might override the set range',
        bannerType: InlineBannerType.WARNING,
        visibilityFn: axisMinMaxBannerVisibilityFn,
      },
      yAxisMin: {
        displayName: 'Min',
        configShapePath: ['chartConfig', 'parameters', 'yAxisMin'],
      },
      yAxisMax: {
        displayName: 'Max',
        configShapePath: ['chartConfig', 'parameters', 'yAxisMax'],
      },
    },
  },
} as const satisfies ShapeUISchemaV2NestedShapeValues;
